import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const setupBenefitsAnimation = (sectionRef, introRef) => {
  const section = sectionRef.current;
  const intro = introRef?.current;

  if (!section) {
    return () => {};
  }

  const cards = Array.from(
    section.querySelectorAll(".autobet-benefit-card, .autobet-benefit-card-mobile")
  );

  if (!cards.length) {
    return () => {};
  }

  let ticking = false;

  // ---------------------------------------------------------
  // CARDS ANIMATION
  // ---------------------------------------------------------

  const updateCards = () => {
    if (ticking) {
      return;
    }

    ticking = true;

    requestAnimationFrame(() => {
      const viewportCenter = window.innerHeight * 0.55;
      const maxDist =
        window.innerWidth <= 600 ? 280 : 260;

      const visibleCards = cards.filter(
        (card) => window.getComputedStyle(card).display !== "none"
      );

      let closestCard = null;
      let closestDistance = Infinity;

      visibleCards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const cardCenter =
          rect.top + rect.height / 2;

        const distance = Math.abs(
          cardCenter - viewportCenter
        );

        if (
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          distance < closestDistance
        ) {
          closestDistance = distance;
          closestCard = card;
        }
      });

      // -----------------------------------------------------
      // ANIMATE ALL VISIBLE CARDS
      // -----------------------------------------------------

      visibleCards.forEach((card, index) => {
        card.classList.toggle(
          "is-active",
          card === closestCard
        );

        const rect = card.getBoundingClientRect();

        const cardCenter =
          rect.top + rect.height / 2;

        const distanceFromCenter =
          cardCenter - viewportCenter;

        let normalized = Math.max(
          -2,
          Math.min(
            2,
            distanceFromCenter / maxDist
          )
        );

        /*
         * Keep the last card stable when it reaches
         * the center and the user continues scrolling.
         */
        if (
          index === visibleCards.length - 1 &&
          distanceFromCenter < 0
        ) {
          normalized = 0;
        }

        const distance = Math.abs(normalized);

        let scale = 0.94;
        let opacity = 0.5;
        let rotateX = 0;
        let translateY = 0;

        if (distance <= 1) {
          scale = 1 - distance * 0.06;
          opacity = 1 - distance * 0.3;

          /*
           * Very subtle depth effect.
           */
          rotateX =
            -Math.sign(normalized) *
            (distance * 20);

          translateY = normalized * -5;
        } else {
          scale = 0.94;
          opacity = 0.65;

          rotateX =
            -Math.sign(normalized) *
            ((2 - distance) * 20);

          translateY =
            Math.sign(normalized) * -5;
        }

        gsap.to(card, {
          duration: 0.4,
          ease: "power2.out",
          scale,
          opacity,
          rotateX,
          y: translateY,
          transformPerspective: 1400,
          overwrite: "auto",
        });
      });

      ticking = false;
    });
  };


  // ---------------------------------------------------------
  // RESIZE
  // ---------------------------------------------------------

  const resizeHandler = () => {
    updateCards();
    ScrollTrigger.refresh();
  };

  window.addEventListener(
    "scroll",
    updateCards,
    {
      passive: true,
    }
  );

  window.addEventListener(
    "resize",
    resizeHandler
  );

  // ---------------------------------------------------------
  // GSAP / SCROLLTRIGGER
  // ---------------------------------------------------------

  const ctx = gsap.context(() => {
    // Initial state
    updateCards();
    ScrollTrigger.refresh();
  }, section);

  // ---------------------------------------------------------
  // CLEANUP
  // ---------------------------------------------------------

  return () => {
    window.removeEventListener(
      "scroll",
      updateCards
    );

    window.removeEventListener(
      "resize",
      resizeHandler
    );

    ctx.revert();
  };
};